import type { AuthenticatedUser } from '@src/features/users';
import {
  jobTitleFromDto,
  teamFromDto,
  workingTimeFromDto,
} from '@src/features/users';
import { isoWeekday } from '@src/shared/lib';
import type { ResourceUser, ResourceUserDto } from '../types';

export function resourceUserFromDto(user: ResourceUserDto): ResourceUser {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    firstName: user.user_first_name,
    familyName: user.user_family_name,
    language: user.user_language,
    managerId: user.user_user_manager,
    hrId: user.user_user_hr,
    branchId: user.user_branch,
    jobTitle: user.user_job_title ? jobTitleFromDto(user.user_job_title) : null,
    team: user.user_team ? teamFromDto(user.user_team) : null,
    portrait: user.user_portrait,
    salaryPercentage: user.salary_percentage,
    worktime: user.worktime.map((w) => workingTimeFromDto(w)),
    vacationUsed: user.vacation_used,
    vacationAvailable: user.vacation_available,
  };
}
export function userResourceId(
  userId: number | null | undefined,
): string | undefined {
  if (userId) return `user-${userId}`;
}

type BusinessHours = {
  daysOfWeek: number[];
  startTime: string;
  endTime: string;
};
type Resource = {
  id: string;
  title: string;
  businessHours: BusinessHours[];
  manager: number | null;
  jobTitle: string | null;
  salaryPercentage: number;
  portrait: string | null;
  order: number;
  userId: number;
  team: string;
  vacationUsed: number;
  vacationAvailable: number;
};

function getBuisnessHoursForResrouce(resource: ResourceUser): BusinessHours[] {
  let buisinessHours: BusinessHours[] = [];
  if (!resource.worktime || !resource.worktime.length) {
    return buisinessHours;
  }
  const toMinutes = (t: string) => {
    if (!t) {
      return null;
    }
    const [hh, mm] = t.split(':').map(Number);
    return hh * 60 + mm;
  };

  const fromMinutes = (m: number) => {
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h}:${min.toString().padStart(2, '0')}`;
  };

  const intervals: Array<{ start: number; end: number; day: number }> = [];
  for (const row of resource.worktime || []) {
    const day = row.weekDay;

    const ms = toMinutes(row.starTimeMorning);
    const me = toMinutes(row.endTimeMorning);
    if (ms != null && me != null && me > ms) {
      intervals.push({ start: ms, end: me, day });
    }

    const as = toMinutes(row.startTimeAfternoon);
    const ae = toMinutes(row.endTimeAfternoon);
    if (as != null && ae != null && ae > as) {
      intervals.push({ start: as, end: ae, day });
    }
  }

  const map: Map<string, { start: number; end: number; days: Set<number> }> =
    new Map();
  for (const it of intervals) {
    const key = `${it.start}-${it.end}`;
    if (!map.has(key)) {
      map.set(key, { start: it.start, end: it.end, days: new Set() });
    }
    const entry = map.get(key);
    if (entry) entry.days.add(it.day);
  }

  buisinessHours = Array.from(map.values()).map(({ start, end, days }) => ({
    startTime: fromMinutes(start),
    endTime: fromMinutes(end),
    daysOfWeek: Array.from(days).sort((a: number, b: number) => a - b),
  }));

  buisinessHours.sort((a, b) => {
    const sa = a.startTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    const sb = b.startTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    if (sa !== sb) {
      return sa - sb;
    }
    const ea = a.endTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    const eb = b.endTime.split(':').reduce((h, i) => h * 60 + Number(i), 0);
    return ea - eb;
  });
  return buisinessHours;
}

export function usersToCalendarResources(
  users: ResourceUser[],
  currentUser: AuthenticatedUser | null,
) {
  if (!users?.length) {
    return [];
  }
  const currentManagerId =
    typeof currentUser?.managerId === 'object' && currentUser?.managerId
      ? (currentUser.managerId as unknown as { id: number }).id
      : currentUser?.managerId;

  const tree = users.map((item: ResourceUser): Resource => {
    const idStr = `user-${String(item.id)}`;
    const title = (item: ResourceUser) => {
      if (item.familyName && item.firstName) {
        return `${item.familyName}, ${item.firstName}`;
      } else {
        return item.name;
      }
    };
    const isCurrentUser = item.id === currentUser?.id;
    const isManager =
      item.id === currentManagerId &&
      currentManagerId !== currentUser?.id;

    let team: string;
    if (isCurrentUser || isManager) {
      team = '';
    } else if (item.team) {
      const sort = String(item.team.sort).padStart(4, '0');
      team = `${sort}:${item.team.name}`;
    } else {
      team = '0000:';
    }

    const resource: Resource = {
      id: idStr,
      title: title(item),
      businessHours: getBuisnessHoursForResrouce(item),
      manager: item.managerId,
      jobTitle: item.jobTitle?.title ?? null,
      portrait: item.portrait,
      salaryPercentage: item.salaryPercentage,
      order: isCurrentUser ? 0 : isManager ? 1 : 2,
      userId: item.id,
      team,
      vacationUsed: item.vacationUsed,
      vacationAvailable: item.vacationAvailable,
    };
    return resource;
  });
  return tree;
}

export function getUserWorktimeForDate(
  userId: number,
  users: ResourceUser[],
  date: string,
) {
  if (!userId || !users || !users.length || !date) {
    return null;
  }

  const user = users.find((u) => u.id === userId);
  if (!user || !user.worktime || !user.worktime.length) {
    return null;
  }

  const dayNumber = isoWeekday(date);
  const dayWorkingTime = user.worktime.find((w) => w.weekDay === dayNumber);
  if (!dayWorkingTime) {
    return null;
  }
  const startTime = dayWorkingTime.starTimeMorning;
  const endTime = dayWorkingTime.endTimeAfternoon;
  return { start: startTime, end: endTime };
}

export function getManagedAbsenceUsers(
  users: ResourceUser[],
  currentUser: AuthenticatedUser | null,
) {
  if (!currentUser) {
    return [];
  }

  if (currentUser.isAdmin) {
    return users;
  }

  return users.filter(
    (user) =>
      user.id === currentUser.id ||
      user.managerId === currentUser.id ||
      user.hrId === currentUser.id,
  );
}

export function canManageAbsenceUser(
  targetUserId: number | null | undefined,
  users: ResourceUser[],
  currentUser: AuthenticatedUser | null,
) {
  if (!currentUser || !targetUserId) {
    return false;
  }

  if (currentUser.isAdmin) {
    return true;
  }

  if (targetUserId === currentUser.id) {
    return true;
  }

  return users.some(
    (user) =>
      user.id === targetUserId &&
      (user.managerId === currentUser.id || user.hrId === currentUser.id),
  );
}
